import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { CategoryDto } from '../types/categoryTypes';
import { iconUrl } from '../config';
import '../styles/CategoryCard.css';

interface CategoryCardProps {
  category: CategoryDto;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onToggle: (id: number) => void;
  onFocusCard?: (id: number) => void;
  isExpanded: boolean;
  department: string;
  isAdmin?: boolean;
}

export function CategoryCard({
  category,
  onEdit,
  onDelete,
  onToggle,
  onFocusCard,
  isExpanded,
  department,
  isAdmin = false,
}: CategoryCardProps): JSX.Element {
  const hasSubCategories = category.subCategories.length > 0;
  const navigate = useNavigate();
  const displayName = category.nameEn ?? category.nameHe;

  function productsPath(): string {
    const base = `/categories/${category.id}/products`;
    return department ? `${base}?department=${encodeURIComponent(department)}` : base;
  }

  function handleCardClick(e: React.MouseEvent<HTMLDivElement>): void {
    // Only toggle if the click is not on an action button
    const target = e.target as HTMLElement;
    if (target.closest('.card-actions')) return;
    if (target.closest('.card-view-products')) return;
    if (hasSubCategories) {
      onToggle(category.id);
    } else {
      navigate(productsPath());
    }
  }

  return (
    <div
      className={`category-card${hasSubCategories ? ' category-card--expandable' : ''}${isExpanded ? ' category-card--expanded' : ''}`}
      onClick={handleCardClick}
      onMouseEnter={() => onFocusCard?.(category.id)}
      onFocus={() => onFocusCard?.(category.id)}
      role={hasSubCategories ? 'button' : undefined}
      tabIndex={hasSubCategories ? 0 : undefined}
      onKeyDown={hasSubCategories ? (e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(category.id); } : undefined}
      aria-expanded={hasSubCategories ? isExpanded : undefined}
      aria-label={hasSubCategories ? `Toggle ${displayName} category` : undefined}
    >
      {/* Action buttons — admin only */}
      {isAdmin && (
        <div className="card-actions">
          <button
            className="card-action-btn"
            onClick={(e) => { e.stopPropagation(); onEdit(category.id); }}
            title="ערוך קטגוריה"
            aria-label={`Edit ${displayName}`}
          >
            &#9998;
          </button>
          <button
            className="card-action-btn card-action-btn--delete"
            onClick={(e) => { e.stopPropagation(); onDelete(category.id); }}
            title="מחק קטגוריה"
            aria-label={`Delete ${displayName}`}
          >
            &#128465;
          </button>
        </div>
      )}

      {/* Chevron indicator */}
      {hasSubCategories && (
        <div className={`card-chevron${isExpanded ? ' card-chevron--expanded' : ''}`} aria-hidden="true">
          &#8964;
        </div>
      )}

      {/* Icon */}
      {iconUrl(category.iconUrl) !== null ? (
        <img
          src={iconUrl(category.iconUrl)!}
          alt={displayName}
          className="card-icon"
        />
      ) : (
        <div className="card-icon-placeholder" aria-hidden="true" />
      )}

      {/* Names — Hebrew bold+large, English small */}
      <p className="card-name-he">{category.nameHe}</p>
      {category.nameEn && <p className="card-name-en">{category.nameEn}</p>}

      {/* Description tooltip — floats on hover/focus */}
      {category.description && (
        <div className="card-description-tooltip" role="tooltip">
          {category.description}
        </div>
      )}

      {/* Sub-category count badge */}
      {hasSubCategories && (
        <span className="card-sub-badge">
          {category.subCategories.length} {category.subCategories.length === 1 ? 'תת קטגוריה' : 'תתי קטגוריות'}
        </span>
      )}

      {/* View products link — only for leaf categories (no subcategories) that have products */}
      {!hasSubCategories && category.productCount > 0 && (
        <button
          className="card-view-products"
          onClick={(e) => {
            e.stopPropagation();
            navigate(productsPath());
          }}
          title={`צפה במוצרים של ${displayName}`}
        >
          צפה במוצרים &#8594;
        </button>
      )}
    </div>
  );
}
